/**
 * Configuration management for the Fyncall Widget
 * Handles merging of user config, tenant config, and defaults
 */

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG = {
    // Required
    baseUrl: 'https://api.fyncall.com',

    // Appearance
    primaryColor: '#007bff',
    secondaryColor: '#6c757d',
    textColor: '#ffffff',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    widgetSize: 'medium',
    widgetWidth: null,
    widgetHeight: null,

    // Branding
    title: 'Chat with us',
    subtitle: 'Usually replies in minutes',
    agentName: 'Support Team',
    agentAvatarUrl: null,
    companyLogo: null,
    companyName: '',
    useCompanyNameAsHeader: true,
    headerTitle: '',

    // Launcher
    showLauncher: true,
    launcherStyle: 'circle',
    launcherSize: 'medium',
    launcherAnimation: 'none',
    launcherText: null,
    launcherIcon: 'chat',
    customLauncherIconUrl: null,
    launcherPositionOffset: { x: 20, y: 20 },

    // Behavior
    autoOpen: false,
    autoOpenDelay: 3000,
    soundNotifications: true,

    // Features
    enableFileUpload: true,
    enableReactions: true,
    enableVoice: false,
    showAgentAvatar: true,
    showTypingIndicator: true,
    showReadReceipts: true,
    showNotificationBadge: true,
    transcriptEnabled: false,

    // Header controls
    showSoundToggle: false,
    showDarkModeToggle: false,

    // Messages
    welcomeMessage: 'Welcome! How can we help you today?',
    inputPlaceholder: 'Type your message...',

    // Home screen
    homeScreenEnabled: true,
    welcomeTitle: 'Hi there! 👋',
    welcomeSubtitle: 'How can we help you today?',
    startChatText: 'Start a conversation',
    homeShowFaqs: true,
    headerCurveStyle: 'wave',

    // Powered by
    showPoweredBy: true,
    poweredByText: 'Powered by Fyncall',

    // Proactive
    enableProactive: true,
    proactiveEnabled: false,
    proactiveDelay: 30000,
    proactiveMessage: "👋 Need any help? Feel free to ask!",

    // Offline
    offlineEnabled: true,
    offlineTitle: "We're away",
    offlineMessage: "Leave us a message and we'll get back to you soon.",
    offlineEmailEnabled: true,

    // Visual enhancements
    gradientEnabled: false,
    gradientColors: ['#007bff', '#6610f2'],
    headerStyle: 'solid',
    headerImageUrl: null,
    blurBackground: false,
    messageBubbleStyle: 'rounded',
    shadowIntensity: 'medium',
    animationStyle: 'subtle',
    messageAnimations: true,
    timestampOnHover: false,
    readReceiptsStyle: 'checkmarks',
    desktopNotifications: false,

    // Quick replies and message styling (from tenant config)
    quickReplies: {},
    messageStyling: {}
};

/**
 * Current widget configuration
 * @type {Object}
 */
let config = { ...DEFAULT_CONFIG };

/**
 * Tenant configuration from server
 * @type {Object|null}
 */
let tenantConfig = null;

/**
 * Get the current configuration
 * @returns {Object} Current config
 */
export function getConfig() {
    return config;
}

/**
 * Get tenant configuration
 * @returns {Object|null} Tenant config
 */
export function getTenantConfig() {
    return tenantConfig;
}

/**
 * Set tenant configuration
 * @param {Object} serverConfig - Configuration from server
 */
export function setTenantConfig(serverConfig) {
    tenantConfig = serverConfig;
}

/**
 * Initialize configuration with user config
 * @param {Object} userConfig - User-provided configuration
 * @returns {Object} Merged configuration
 */
export function initConfig(userConfig) {
    config = {
        ...DEFAULT_CONFIG,
        ...userConfig
    };
    return config;
}

/**
 * Apply tenant configuration from server
 * @param {Object} serverConfig - Configuration from server
 * @returns {Object} Updated configuration
 */
export function applyTenantConfig(serverConfig) {
    tenantConfig = serverConfig;

    const appearance = serverConfig.appearance || {};
    const behavior = serverConfig.behavior || {};
    const messages = serverConfig.messages || {};
    const offline = serverConfig.offline || {};
    const proactive = serverConfig.proactive || {};
    const quickReplies = serverConfig.quick_replies || {};

    // Merge server config with existing config
    config = {
        ...config,
        // Theme settings
        primaryColor: appearance.primary_color || config.primaryColor,
        secondaryColor: appearance.secondary_color || config.secondaryColor,
        textColor: appearance.text_color || config.textColor,
        backgroundColor: appearance.background_color || config.backgroundColor,
        borderRadius: appearance.border_radius || config.borderRadius,
        widgetSize: appearance.widget_size || config.widgetSize,
        widgetWidth: appearance.widget_width || config.widgetWidth,
        widgetHeight: appearance.widget_height || config.widgetHeight,

        // Appearance settings
        title: appearance.title || config.title,
        subtitle: appearance.subtitle || config.subtitle,
        agentName: appearance.agent_name || config.agentName,
        agentAvatarUrl: appearance.agent_avatar_url || config.agentAvatarUrl,
        companyLogo: appearance.company_logo_url || config.companyLogo,
        companyName: appearance.company_name || config.companyName,
        useCompanyNameAsHeader: appearance.use_company_name_as_header !== false,
        headerTitle: appearance.header_title || config.headerTitle,
        showAgentAvatar: appearance.show_agent_avatar !== false,
        showTypingIndicator: appearance.show_typing_indicator !== false,
        enableFileUpload: appearance.show_file_upload !== false,

        // Launcher
        launcherStyle: appearance.launcher_style || config.launcherStyle,
        launcherSize: appearance.launcher_size || config.launcherSize,
        launcherAnimation: appearance.launcher_animation || config.launcherAnimation,
        launcherText: appearance.launcher_text || config.launcherText,
        launcherIcon: appearance.launcher_icon || config.launcherIcon,
        customLauncherIconUrl: appearance.custom_launcher_icon_url || config.customLauncherIconUrl,
        launcherPositionOffset: appearance.launcher_position_offset || config.launcherPositionOffset,

        // Header controls
        showSoundToggle: appearance.show_sound_toggle === true,
        showDarkModeToggle: appearance.show_dark_mode_toggle === true,
        showReadReceipts: appearance.show_read_receipts !== false,
        messageAnimations: appearance.message_animations !== false,
        showNotificationBadge: appearance.show_notification_badge !== false,
        transcriptEnabled: appearance.transcript_enabled === true,

        // Visual enhancements
        gradientEnabled: appearance.gradient_enabled === true,
        gradientColors: appearance.gradient_colors || config.gradientColors,
        headerStyle: appearance.header_style || config.headerStyle,
        headerImageUrl: appearance.header_image_url || config.headerImageUrl,
        blurBackground: appearance.blur_background === true,
        messageBubbleStyle: appearance.message_bubble_style || config.messageBubbleStyle,
        shadowIntensity: appearance.shadow_intensity || config.shadowIntensity,
        animationStyle: appearance.animation_style || config.animationStyle,
        headerCurveStyle: appearance.header_curve_style || config.headerCurveStyle,
        timestampOnHover: (serverConfig.message_styling?.timestamp_on_hover) === true,
        readReceiptsStyle: serverConfig.message_styling?.read_receipts_style || config.readReceiptsStyle,
        desktopNotifications: behavior.desktop_notifications === true,

        // Powered by
        showPoweredBy: appearance.show_powered_by !== false,
        poweredByText: appearance.powered_by_text || config.poweredByText,

        // Behavior settings
        autoOpen: behavior.auto_open || config.autoOpen,
        autoOpenDelay: behavior.auto_open_delay || config.autoOpenDelay,
        soundNotifications: behavior.sound_notifications !== false,

        // Message settings
        welcomeMessage: messages.welcome_message || config.welcomeMessage,
        inputPlaceholder: messages.input_placeholder || config.inputPlaceholder,

        // Home screen settings
        homeScreenEnabled: quickReplies.home_screen_enabled !== false,
        welcomeTitle: quickReplies.home_greeting_title || messages.greeting_message || config.welcomeTitle,
        welcomeSubtitle: quickReplies.home_greeting_subtitle || messages.welcome_message || config.welcomeSubtitle,
        startChatText: quickReplies.home_start_chat_text || config.startChatText,
        homeShowFaqs: quickReplies.home_show_faqs !== false,

        // Quick replies and message styling
        quickReplies: quickReplies,
        messageStyling: serverConfig.message_styling || {},

        // Offline mode
        offlineEnabled: offline.enabled !== false,
        offlineTitle: offline.title || config.offlineTitle,
        offlineMessage: offline.message || config.offlineMessage,
        offlineEmailEnabled: offline.email_enabled !== false,

        // Proactive settings
        proactiveEnabled: proactive.enabled === true,
        proactiveDelay: proactive.default_delay || config.proactiveDelay,
        proactiveMessage: proactive.proactive_message || config.proactiveMessage
    };

    return config;
}

/**
 * Get a specific config value
 * @param {string} key - Config key
 * @param {*} defaultValue - Default if not found
 * @returns {*} Config value
 */
export function getConfigValue(key, defaultValue = null) {
    return config[key] !== undefined ? config[key] : defaultValue;
}

/**
 * Update a specific config value
 * @param {string} key - Config key
 * @param {*} value - New value
 */
export function setConfigValue(key, value) {
    config[key] = value;
}

/**
 * Get dark mode colors from tenant config
 * @returns {Object} Dark mode color overrides
 */
export function getDarkModeColors() {
    return tenantConfig?.appearance?.dark_mode_colors || {};
}

/**
 * Get theme preset from tenant config
 * @returns {string} Theme preset name
 */
export function getThemePreset() {
    return tenantConfig?.appearance?.theme_preset || 'light';
}

/**
 * Get pre-chat form configuration
 * @returns {Object|null} Pre-chat form config
 */
export function getPrechatFormConfig() {
    return tenantConfig?.prechat_form || null;
}

/**
 * Get CSAT survey configuration
 * @returns {Object|null} CSAT config
 */
export function getCsatConfig() {
    return tenantConfig?.csat || null;
}

/**
 * Get proactive triggers configuration
 * @returns {Array} Proactive triggers
 */
export function getProactiveTriggers() {
    return tenantConfig?.proactive?.triggers || [];
}

/**
 * Check if we're using ngrok (for headers)
 * @returns {boolean} Is ngrok URL
 */
export function isNgrokUrl() {
    return config.baseUrl?.includes('ngrok') || false;
}

/**
 * Get base URL for API calls
 * @returns {string} Base URL
 */
export function getBaseUrl() {
    return config.baseUrl;
}
